import {
  Component,
  computed,
  inject,
  OnInit,
  OnDestroy,
  effect,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import {
  ProfilelistActions,
  ProfilelistCloneActions,
  profilelistCloneFeature,
  selectProfilelistGridData,
  selectProfilelistDrilldownKey,
  selectProfilelistEditItemKey,
  selectSaving,
} from './profilelist.feature';

import { MainTableComponent } from './components/main-table/main-table.component';
import { ProfileDetailsComponent } from './components/profile-details/profile-details.component';
import { ProfileDataService } from 'src/app/shared/store/features/profile-data/profile-data.service';
import { ProfileFormComponent } from './components/profile-form/profile-form.component';
import { UpdateProfileRequestDto } from 'src/app/shared/services/api/dto/update-profile-request-dto';
import { SpinnerComponent } from '../../shared/components/spinner/spinner.component';
import { CalistDataService } from 'src/app/shared/store/features/calist-data/calist-data.service';
import { map } from 'rxjs';
import { CreateCertificateProfileRequestDto } from 'src/app/shared/services/api/dto/create-certificate-profile-request-dto';

@Component({
  selector: 'app-profilelist',
  imports: [
    ProfileDetailsComponent,
    MainTableComponent,
    ProfileFormComponent,
    SpinnerComponent,
  ],
  templateUrl: './profilelist.component.html',
  styleUrl: './profilelist.component.scss',
})
export class ProfilelistComponent implements OnInit, OnDestroy {
  private store = inject(Store);
  private calistDataService = inject(CalistDataService);
  private profilelistDataService = inject(ProfileDataService);
  private gridData$ = this.store.select(selectProfilelistGridData);
  dataParams = toSignal(this.gridData$);
  private drilldownProfileName = toSignal(
    this.store.select(selectProfilelistDrilldownKey),
  );
  private editProfileName = toSignal(
    this.store.select(selectProfilelistEditItemKey),
  );
  drilldown = computed(() => {
    const drilldownProfileName = this.drilldownProfileName();
    return drilldownProfileName
      ? this.dataParams()?.rowData.find(
          ({ profileName }) => profileName === this.drilldownProfileName(),
        )
      : undefined;
  });
  edit = computed(() => {
    const editProfileName = this.editProfileName();
    return editProfileName
      ? this.dataParams()?.rowData.find(
          ({ profileName }) => profileName === editProfileName,
        )
      : undefined;
  });
  cloneProfileName = toSignal(
    this.store.select(profilelistCloneFeature.selectEditItemKey),
  );
  clone = computed(() => {
    const cloneProfileName = this.cloneProfileName();
    return cloneProfileName
      ? this.dataParams()?.rowData.find(
          ({ profileName }) => profileName === cloneProfileName,
        )
      : undefined;
  });

  profilesLoading = toSignal(this.profilelistDataService.isLoading$);
  calistLoading = toSignal(this.calistDataService.isLoading$);
  certificationAuthorities = toSignal(
    this.calistDataService.data$.pipe(map(calist => calist ?? [])),
    { initialValue: [] },
  );

  saving = toSignal(this.store.select(selectSaving));

  onBack() {
    this.store.dispatch(ProfilelistActions.backFromDrilldown());
  }

  onUpdate(event: UpdateProfileRequestDto) {
    const originalObject = this.edit();
    if (originalObject) {
      this.store.dispatch(
        ProfilelistActions.save({ originalObject, edit: event }),
      );
    }
  }

  onClone(event: CreateCertificateProfileRequestDto) {
    this.store.dispatch(
      ProfilelistCloneActions.save({ originalObject: undefined, edit: event }),
    );
  }

  onCancel(event: { confirmation: boolean }) {
    if (this.clone()) {
      this.store.dispatch(ProfilelistCloneActions.cancel(event));
    } else {
      this.store.dispatch(ProfilelistActions.cancel(event));
    }
  }

  ngOnInit() {
    this.profilelistDataService.ensure();
  }
  ngOnDestroy() {
    this.profilelistDataService.clear();
  }

  private loadCAEffect = () => {
    const edit = this.edit();
    const clone = this.clone();
    if (edit || clone) {
      this.calistDataService.ensure();
    } else {
      this.calistDataService.clear();
    }
  };

  constructor() {
    effect(this.loadCAEffect);
  }
}
